import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Button, CircularProgress, IconButton, Tooltip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import NoteAddOutlinedIcon from "@mui/icons-material/NoteAddOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ConfirmDialog from "components/Modals/ConfirmDialog";
import { P } from "const/permissions";
import { hasPermission } from "utils/permissionUtils";
import { formatDisplayDate } from "utils/dateFormat";
import { getOriginalFileName } from "utils/utils";
import AddDocumentDialog from "views/admin/Admissions/AddAdmissions/applicationFlow/AddDocumentDialog";
import NewDocumentRequestDialog from "views/admin/Clients/documents/NewDocumentRequestDialog";
import {
  CLIENT_DOCUMENT_REQUESTS_QUERY_KEY,
  LINKED_DOCUMENTS_QUERY_KEY,
  downloadDocument,
  fetchDocumentRequests,
  fetchLinkedDocuments,
  unlinkDocumentFromModule,
} from "views/admin/Clients/documents/clientDocuments.api";
import {
  buildEntryDocumentRows,
  countRows,
  entryDocumentRights,
  requestsForEntry,
} from "./entryDocuments";
import useEntryDocumentEditor from "./useEntryDocumentEditor";

const FILTERS = [
  { id: "all", label: "All" },
  { id: "requested", label: "Waiting on client" },
  { id: "client", label: "From client" },
  { id: "uploaded", label: "Uploaded" },
];

const SOURCE = {
  uploaded: { label: "Staff upload", className: "bg-app-surface-muted text-app-text-secondary" },
  client: { label: "From client", className: "bg-sky-50 text-sky-700" },
  requested: { label: "Requested", className: "bg-violet-50 text-violet-700" },
};

const TONES = {
  success: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  warning: "bg-amber-50 text-amber-700 ring-amber-200",
  info: "bg-sky-50 text-sky-700 ring-sky-200",
  danger: "bg-rose-50 text-rose-700 ring-rose-200",
  neutral: "bg-app-surface-muted text-app-text-secondary ring-app-border",
};

/** Document | source | status | date | actions, fixed so rows line up. */
const GRID =
  "md:grid md:grid-cols-[minmax(0,2fr)_7.5rem_11rem_6.5rem_6.5rem] md:items-center md:gap-3";

const safeDate = (value) => {
  if (!value) return "—";
  try {
    return formatDisplayDate(value);
  } catch {
    return "—";
  }
};

const defaultNote = (persisted) =>
  persisted
    ? "Files you add or remove here are saved straight away. Requests go to the client straight away."
    : "Files you add here are saved when you create the record.";

/**
 * The Documents tab of a module entry (Visa, Insurance, RPL, Skill assessment, Admission): what
 * staff uploaded, what the client sent, and what is still being asked of the client, in one
 * table. Requests and client files come from the client's document requests filed into this entry.
 *
 * The entry's own uploads are `documents`. On a saved entry the panel saves each add and remove at
 * once and hands the server's list to `onDocumentsChange`; before the entry exists it only edits
 * that list. Admission saves its documents its own way and passes `onAddClick` / `onRemove`
 * (a promise) instead.
 */
const EntryDocumentsPanel = ({
  moduleType,
  entryId,
  clientId,
  documents = [],
  onDocumentsChange,
  onAddClick,
  onRemove,
  readOnly = false,
  loading = false,
  note,
  extraActions = null,
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState("all");
  const [adding, setAdding] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [confirm, setConfirm] = useState(null); // { type: "remove" | "unlink", row }
  const [openingId, setOpeningId] = useState(null);
  const [removing, setRemoving] = useState(false);

  const editor = useEntryDocumentEditor({ moduleType, entryId, documents, onDocumentsChange });
  const rights = entryDocumentRights({
    moduleType,
    persisted: editor.persisted,
    readOnly,
    can: hasPermission,
  });
  const canAdd = rights.canAdd && Boolean(onAddClick || onDocumentsChange);
  const canRemove = rights.canRemove && Boolean(onRemove || onDocumentsChange);
  const canRequest = hasPermission(P.CLIENT_DOCUMENT_REQUEST.CREATE);
  const canUnlink = hasPermission(P.CLIENT_DOCUMENT.UPDATE);
  const saved = Boolean(entryId && clientId);

  const linkedQuery = useQuery({
    queryKey: [LINKED_DOCUMENTS_QUERY_KEY, moduleType, entryId],
    queryFn: () => fetchLinkedDocuments(moduleType, entryId),
    enabled: Boolean(moduleType && entryId),
    refetchOnMount: "always",
  });

  // Same key the request dialog refreshes, so a new request shows up here straight away.
  const requestsQuery = useQuery({
    queryKey: [CLIENT_DOCUMENT_REQUESTS_QUERY_KEY, clientId],
    queryFn: () => fetchDocumentRequests(clientId),
    enabled: saved,
    refetchOnMount: "always",
  });

  const rows = useMemo(
    () =>
      buildEntryDocumentRows({
        uploads: documents,
        linked: linkedQuery.data || [],
        requests: requestsForEntry(requestsQuery.data || [], moduleType, entryId),
      }),
    [documents, linkedQuery.data, requestsQuery.data, moduleType, entryId]
  );
  const counts = countRows(rows);
  const visible = filter === "all" ? rows : rows.filter((row) => row.kind === filter);
  const busy = loading || linkedQuery.isLoading || (saved && requestsQuery.isLoading);

  const unlink = useMutation({
    mutationFn: (doc) => unlinkDocumentFromModule(clientId, doc.documentId, doc.linkId),
    onSuccess: () => {
      toast.success("Removed from this record. The file is still on the client's Documents tab.");
      queryClient.invalidateQueries({
        queryKey: [LINKED_DOCUMENTS_QUERY_KEY, moduleType, entryId],
      });
    },
    onError: (error) => toast.error(error.message),
    onSettled: () => setConfirm(null),
  });

  // Client files sit in a private bucket and carry no URL, so they stream through the API.
  const openClientFile = async (doc) => {
    setOpeningId(doc.linkId);
    try {
      const { blob } = await downloadDocument(clientId, doc.documentId, doc.originalFilename);
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setOpeningId(null);
    }
  };

  const openRequest = (request) =>
    navigate(`/admin/client/view/${clientId}`, {
      state: { tab: "documents", documentRequestId: request.id },
    });

  const startAdd = () => (onAddClick ? onAddClick() : setAdding(true));

  // Rejects on failure so the add dialog stays open with the file the user just picked.
  const addDocument = async (doc) => {
    await editor.add(doc);
    if (editor.persisted) toast.success("Document saved.");
  };

  const removeDocument = async (doc) => {
    setRemoving(true);
    try {
      await (onRemove ? onRemove(doc) : editor.remove(doc));
      if (editor.persisted) toast.success("Document removed.");
      setConfirm(null);
    } catch (error) {
      toast.error(error?.message || "Could not remove the document. Try again.");
    } finally {
      setRemoving(false);
    }
  };

  const renderActions = (row) => {
    if (row.kind === "uploaded") {
      const doc = row.source;
      return (
        <>
          <Tooltip title="Preview">
            <IconButton component={Link} to={doc.url} target="_blank" size="small">
              <VisibilityOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Download">
            <IconButton component={Link} to={doc.url} target="_blank" download size="small">
              <FileDownloadOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {canRemove && !doc.readOnly ? (
            <Tooltip title="Remove">
              <IconButton
                size="small"
                color="error"
                onClick={() => setConfirm({ type: "remove", row })}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
        </>
      );
    }
    if (row.kind === "client") {
      const doc = row.source;
      return (
        <>
          <Tooltip title="Open file">
            <span>
              <IconButton
                size="small"
                disabled={openingId === doc.linkId}
                onClick={() => openClientFile(doc)}
              >
                {openingId === doc.linkId ? (
                  <CircularProgress size={14} />
                ) : (
                  <VisibilityOutlinedIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
          {canUnlink ? (
            <Tooltip title="Stop showing this here">
              <IconButton size="small" onClick={() => setConfirm({ type: "unlink", row })}>
                <LinkOffIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
        </>
      );
    }
    return (
      <Tooltip title="Open the request on the client's Documents tab">
        <IconButton size="small" onClick={() => openRequest(row.source.request)}>
          <OpenInNewIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    );
  };

  return (
    <section className="overflow-hidden rounded-xl border border-app-border bg-app-surface">
      <header className="flex flex-wrap items-start justify-between gap-3 border-b border-app-border px-4 py-3.5">
        <div className="min-w-0">
          <h3 className="m-0 flex items-center gap-2 text-base font-semibold text-app-text">
            Documents
            <span className="rounded-full bg-app-surface-muted px-2 py-0.5 text-xs font-semibold tabular-nums text-app-text-secondary">
              {counts.all}
            </span>
            {counts.waiting ? (
              <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700">
                {counts.waiting} waiting on client
              </span>
            ) : null}
          </h3>
          <p className="m-0 mt-1 text-xs text-app-text-muted">
            {note || defaultNote(editor.persisted)}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {extraActions}
          {canRequest ? (
            <Tooltip title={saved ? "" : "Save the record first"}>
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<NoteAddOutlinedIcon />}
                  disabled={!saved}
                  onClick={() => setRequesting(true)}
                >
                  Request from client
                </Button>
              </span>
            </Tooltip>
          ) : null}
          {canAdd ? (
            <Button
              size="small"
              variant="contained"
              disableElevation
              startIcon={<AddIcon />}
              onClick={startAdd}
            >
              Add document
            </Button>
          ) : null}
        </div>
      </header>

      {rows.length ? (
        <div className="flex flex-wrap gap-2 border-b border-app-border px-4 py-2.5">
          {FILTERS.map((option) => {
            const active = filter === option.id;
            return (
              <button
                key={option.id}
                type="button"
                onClick={() => setFilter(option.id)}
                className={[
                  "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold cursor-pointer transition-colors",
                  active
                    ? "border-primary bg-primary text-white"
                    : "border-app-border bg-app-surface text-app-text-secondary hover:text-app-text",
                ].join(" ")}
              >
                {option.label}
                <span className="tabular-nums opacity-80">{counts[option.id] ?? 0}</span>
              </button>
            );
          })}
        </div>
      ) : null}

      {busy && !rows.length ? (
        <div className="flex justify-center py-10">
          <CircularProgress size={24} />
        </div>
      ) : !rows.length ? (
        <div className="px-4 py-10 text-center">
          <InsertDriveFileOutlinedIcon className="text-app-text-muted" sx={{ fontSize: 40 }} />
          <p className="m-0 mt-2 text-sm font-medium text-app-text">No documents yet</p>
          <p className="m-0 mt-1 text-xs text-app-text-muted">
            Add a file you already have, or ask the client to upload what this record needs.
          </p>
        </div>
      ) : (
        <div>
          <div
            className={`hidden ${GRID} bg-app-surface-muted px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-app-text-muted`}
          >
            <span>Document</span>
            <span>Source</span>
            <span>Status</span>
            <span>Date</span>
            <span className="text-right">Actions</span>
          </div>
          {visible.length ? (
            <ul className="m-0 list-none divide-y divide-app-border p-0">
              {visible.map((row) => (
                <li
                  key={row.key}
                  className={`${GRID} flex flex-wrap items-center gap-x-3 gap-y-1.5 px-4 py-3`}
                >
                  <div className="min-w-0 basis-full md:basis-auto">
                    <p className="m-0 flex items-center gap-1.5 truncate text-sm font-medium text-app-text">
                      <span className="truncate">{row.name}</span>
                      {row.optional ? (
                        <span className="shrink-0 text-xs font-normal text-app-text-muted">
                          (optional)
                        </span>
                      ) : null}
                    </p>
                    <p className="m-0 truncate text-xs text-app-text-muted">
                      {row.kind === "uploaded"
                        ? getOriginalFileName(row.detail)
                        : row.kind === "requested"
                        ? `In “${row.detail || "Document request"}”`
                        : row.detail}
                      {row.by && row.kind === "uploaded" ? ` · ${row.by}` : ""}
                    </p>
                  </div>
                  <span>
                    <span
                      className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                        SOURCE[row.kind].className
                      }`}
                    >
                      {SOURCE[row.kind].label}
                    </span>
                  </span>
                  <span className="flex items-center gap-1">
                    <span
                      className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset ${
                        TONES[row.status.tone] || TONES.neutral
                      }`}
                    >
                      {row.status.label}
                    </span>
                    {row.stale ? (
                      <Tooltip title="Waiting on the client for over 30 days">
                        <WarningAmberIcon sx={{ fontSize: 16 }} className="text-amber-500" />
                      </Tooltip>
                    ) : null}
                  </span>
                  <span className="text-xs text-app-text-muted whitespace-nowrap">
                    {safeDate(row.date)}
                  </span>
                  <span className="ml-auto flex items-center justify-end md:ml-0">
                    {renderActions(row)}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="m-0 px-4 py-8 text-center text-sm text-app-text-muted">
              Nothing here under this filter.
            </p>
          )}
        </div>
      )}

      {onDocumentsChange && !onAddClick ? (
        <AddDocumentDialog open={adding} onClose={() => setAdding(false)} onAdd={addDocument} />
      ) : null}

      {requesting ? (
        <NewDocumentRequestDialog
          open
          clientId={clientId}
          defaultTarget={{ moduleType, entryId }}
          onClose={() => setRequesting(false)}
        />
      ) : null}

      <ConfirmDialog
        open={Boolean(confirm)}
        title={
          confirm?.type === "unlink" ? "Stop showing this document here?" : "Remove this document?"
        }
        message={
          confirm?.type === "unlink"
            ? `"${confirm.row.name}" stops appearing on this record. The file stays on the client's Documents tab and is not deleted.`
            : `"${confirm?.row.name}" will be removed from this record.`
        }
        confirmLabel={confirm?.type === "unlink" ? "Remove from record" : "Remove"}
        confirmColor="error"
        loading={unlink.isLoading || removing}
        onConfirm={() => {
          if (confirm.type === "unlink") {
            unlink.mutate(confirm.row.source);
          } else {
            removeDocument(confirm.row.source);
          }
        }}
        onCancel={() => setConfirm(null)}
      />
    </section>
  );
};

export default EntryDocumentsPanel;
